<template>
  <div class="order-risk-trace-container">
    <!-- 风控概览仪表盘 -->
    <transition name="fade-slide">
      <div v-if="riskData" class="risk-dashboard">
        <!-- 风险等级主卡片 -->
        <div class="risk-main-card" :class="getRiskLevelClass(riskData.riskLevel)">
          <div class="risk-glow"></div>
          <div class="risk-pulse">
            <span class="risk-level-in-circle">{{ riskData.riskLevel || '未知' }}</span>
          </div>
          <div class="risk-icon-wrapper">
            <i class="el-icon-warning-outline risk-icon"></i>
            <span class="risk-label-inline">RISK LEVEL</span>
          </div>
          <div class="risk-content">
            <div class="risk-score-bar">
              <div class="score-label">风险分值</div>
              <div class="score-value">{{ riskData.totalScore || 0 }}</div>
              <div class="score-bar-bg">
                <div class="score-bar-fill" :style="{ width: ((riskData.totalScore || 0) > 0 ? Math.min((riskData.totalScore || 0), 100) + '%' : '5%') }"></div>
              </div>
            </div>
          </div>
        </div>

        <!-- 订单信息卡片 -->
        <div class="info-card">
          <div class="info-card-header">
            <div class="info-icon order-icon">
              <i class="el-icon-document"></i>
            </div>
            <div class="info-title">订单信息</div>
          </div>
          <div class="info-body">
            <div class="info-row">
              <span class="info-label">订单号</span>
              <span class="info-value">{{ riskData.orderNo }}</span>
            </div>
            <div class="info-row">
              <span class="info-label">订单金额</span>
              <span class="info-value amount">{{riskData.symbol}}{{ riskData.orderAmount }}</span>
            </div>
          </div>
        </div>

        <!-- 时间信息卡片 -->
        <div class="info-card">
          <div class="info-card-header">
            <div class="info-icon time-icon">
              <i class="el-icon-time"></i>
            </div>
            <div class="info-title">检测时间</div>
          </div>
          <div class="info-body">
            <div class="info-row single">
              <span class="info-value time">{{ riskData.checkTime }}</span>
            </div>
          </div>
        </div>
      </div>
    </transition>

    <!-- 详细信息区域 -->
    <transition-group name="stagger" tag="div" v-if="riskData" class="detail-sections">
      <!-- 命中规则 -->
      <div key="rules" class="detail-card rules-card">
        <div class="card-decoration-line"></div>
        <div class="detail-header">
          <div class="header-icon warning-icon">
            <i class="el-icon-warning"></i>
          </div>
          <div class="header-text">
            <h3>命中规则</h3>
            <p>Hit Rules - {{ hitRules.length }} 条规则命中</p>
          </div>
        </div>
        <div class="table-wrapper">
          <el-table :data="hitRules" class="risk-table" stripe>
            <el-table-column prop="ruleName" label="规则名称" width="200">
              <template slot-scope="scope">
                <div class="rule-name-cell">
                  <span class="rule-dot"></span>
                  {{ scope.row.ruleName }}
                </div>
              </template>
            </el-table-column>
            <el-table-column prop="reason" label="规则描述" min-width="200"></el-table-column>
            <el-table-column prop="score" label="风险分值" width="140" align="center">
              <template slot-scope="scope">
                <div class="score-cell" :class="getScoreCellClass(scope.row.score)">
                  <span class="score-number">{{ scope.row.score }}</span>
                  <span class="score-unit">分</span>
                </div>
              </template>
            </el-table-column>
          </el-table>
        </div>
      </div>

      <!-- 设备与位置信息 -->
      <div key="device" class="detail-card device-card">
        <div class="card-decoration-line"></div>
        <div class="detail-header">
          <div class="header-icon location-icon">
            <i class="el-icon-location-information"></i>
          </div>
          <div class="header-text">
            <h3>设备与位置</h3>
            <p>Device & Location Information</p>
          </div>
        </div>
        <div class="info-grid">
          <div class="info-item-card">
            <div class="item-label">设备 ID</div>
            <div class="item-value">{{ riskData.deviceId || '-' }}</div>
          </div>
          <div class="info-item-card">
            <div class="item-label">IP 地址</div>
            <div class="item-value ip">{{ riskData.ipAddress || '-' }}</div>
          </div>
          <div class="info-item-card full-width">
            <div class="item-label">地理位置</div>
            <div class="item-value">{{ riskData.location || '-' }}</div>
          </div>
        </div>
      </div>

      <!-- 用户信息 -->
      <div key="user" class="detail-card user-card">
        <div class="card-decoration-line"></div>
        <div class="detail-header">
          <div class="header-icon user-icon">
            <i class="el-icon-user"></i>
          </div>
          <div class="header-text">
            <h3>用户信息</h3>
            <p>User Information</p>
          </div>
        </div>
        <div class="info-grid">
          <div class="info-item-card">
            <div class="item-label">用户 ID</div>
            <div class="item-value">{{ riskData.userId }}</div>
          </div>
          <div class="info-item-card">
            <div class="item-label">用户姓名</div>
            <div class="item-value">{{ riskData.userName || '-' }}</div>
          </div>
          <div class="info-item-card">
            <div class="item-label">手机号</div>
            <div class="item-value phone">{{ riskData.userPhone || '-' }}</div>
          </div>
        </div>
      </div>

      <!-- 航班信息 -->
      <div v-if="flightInfo" key="flight" class="detail-card flight-card">
        <div class="card-decoration-line"></div>
        <div class="detail-header">
          <div class="header-icon flight-icon">
            <i class="el-icon-s-promotion"></i>
          </div>
          <div class="header-text">
            <h3>航班信息</h3>
            <p>Flight Information</p>
          </div>
        </div>

        <div v-if="!hasFlightInfo" class="flight-empty">
          <div class="empty-icon">✈️</div>
          <p>暂无航班信息</p>
        </div>

        <div v-else class="flight-info-wrapper">
          <!-- 去程 -->
          <div v-if="flightInfo.fromSegment && flightInfo.fromSegment.length" class="flight-section">
            <div class="flight-section-header">
              <div class="flight-icon-bg departure">
                <i class="el-icon-right"></i>
              </div>
              <div class="flight-header-text">
                <div class="flight-title">去程航班</div>
                <div class="flight-subtitle">共 {{ flightInfo.fromSegment.length }} 段行程</div>
              </div>
            </div>
            <div class="flight-segments">
              <div v-for="(segment, index) in flightInfo.fromSegment" :key="'from-' + index" class="flight-segment">
                <div class="segment-number">第 {{ index + 1 }} 段</div>
                <el-tag size="small" type="primary" class="flight-number-tag">{{ segment.flightNumber }}</el-tag>
                <div class="segment-route">
                  <div class="airport-dep">
                    <div class="code">{{ segment.depAirportCode }}</div>
                    <div class="time">{{ formatFlightTime(segment.depTime) }}</div>
                  </div>
                  <div class="route-arrow">
                    <div class="arrow-line"></div>
                    <i class="el-icon-d-arrow-right"></i>
                  </div>
                  <div class="airport-arr">
                    <div class="code">{{ segment.arrAirportCode }}</div>
                    <div class="time">{{ formatFlightTime(segment.arrTime) }}</div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- 返程 -->
          <div v-if="flightInfo.retSegment && flightInfo.retSegment.length" class="flight-section return">
            <div class="flight-section-header">
              <div class="flight-icon-bg return">
                <i class="el-icon-back"></i>
              </div>
              <div class="flight-header-text">
                <div class="flight-title">返程航班</div>
                <div class="flight-subtitle">共 {{ flightInfo.retSegment.length }} 段行程</div>
              </div>
            </div>
            <div class="flight-segments">
              <div v-for="(segment, index) in flightInfo.retSegment" :key="'ret-' + index" class="flight-segment">
                <div class="segment-number">第 {{ index + 1 }} 段</div>
                <el-tag size="small" type="success" class="flight-number-tag">{{ segment.flightNumber }}</el-tag>
                <div class="segment-route">
                  <div class="airport-dep">
                    <div class="code">{{ segment.depAirportCode }}</div>
                    <div class="time">{{ formatFlightTime(segment.depTime) }}</div>
                  </div>
                  <div class="route-arrow">
                    <div class="arrow-line"></div>
                    <i class="el-icon-d-arrow-right"></i>
                  </div>
                  <div class="airport-arr">
                    <div class="code">{{ segment.arrAirportCode }}</div>
                    <div class="time">{{ formatFlightTime(segment.arrTime) }}</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- 全部规则检测结果 -->
      <div key="allrules" class="detail-card allrules-card">
        <div class="card-decoration-line"></div>
        <div class="detail-header">
          <div class="header-icon grid-icon">
            <i class="el-icon-s-grid"></i>
          </div>
          <div class="header-text">
            <h3>全部规则检测结果</h3>
            <p>All Rules Detection Results - {{ allRulesResult.length }} 条规则</p>
          </div>
        </div>
        <div class="table-wrapper">
          <el-table :data="allRulesResult" class="risk-table" stripe>
            <el-table-column prop="ruleName" label="规则名称" width="200">
              <template slot-scope="scope">
                <div class="rule-name-cell">
                  <span class="rule-dot" :class="{ hit: scope.row.hit }"></span>
                  {{ scope.row.ruleName }}
                </div>
              </template>
            </el-table-column>
            <el-table-column prop="result" label="检测结果" width="120" align="center">
              <template slot-scope="scope">
                <div class="result-badge" :class="scope.row.hit ? 'hit' : 'safe'">
                  <i :class="scope.row.hit ? 'el-icon-warning' : 'el-icon-success'"></i>
                  <span>{{ scope.row.hit ? '命中' : '未命中' }}</span>
                </div>
              </template>
            </el-table-column>
            <el-table-column prop="score" label="分值" width="100" align="center">
              <template slot-scope="scope">
                <span class="score-value">{{ scope.row.score }}</span>
              </template>
            </el-table-column>
            <el-table-column prop="detail" label="详情" min-width="200"></el-table-column>
          </el-table>
        </div>
      </div>
    </transition-group>

    <!-- 空状态 -->
    <transition name="fade">
      <div v-if="!riskData && !loading" class="empty-state">
        <div class="empty-illustration">
          <div class="pulse-ring"></div>
          <div class="pulse-ring delay-1"></div>
          <div class="pulse-ring delay-2"></div>
          <i class="el-icon-warning-outline"></i>
        </div>
        <h2>暂无数据</h2>
        <p>未能获取到风控信息</p>
      </div>
    </transition>
  </div>
</template>

<script>
import { queryOrderRiskTrace } from '@/api/risk/riskApi'

export default {
  name: 'orderRiskTrace',

  data () {
    return {
      orderNo: '',
      loading: false,
      riskData: null
    }
  },

  computed: {
    hitRules () {
      if (!this.riskData || !this.riskData.hitRules) return []
      try {
        return JSON.parse(this.riskData.hitRules)
      } catch (e) {
        return []
      }
    },
    flightInfo () {
      if (!this.riskData || !this.riskData.flightInfo) return null
      try {
        return JSON.parse(this.riskData.flightInfo)
      } catch (e) {
        return null
      }
    },
    hasFlightInfo () {
      if (!this.flightInfo) return false
      const hasFrom = this.flightInfo.fromSegment && this.flightInfo.fromSegment.length > 0
      const hasRet = this.flightInfo.retSegment && this.flightInfo.retSegment.length > 0
      return hasFrom || hasRet
    },
    allRulesResult () {
      if (!this.riskData || !this.riskData.allRulesResult) return []
      try {
        const result = JSON.parse(this.riskData.allRulesResult)
        return Object.keys(result).map(key => ({
          ruleName: key,
          ...result[key]
        }))
      } catch (e) {
        return []
      }
    }
  },

  created () {
    // 从URL参数获取订单号并自动查询
    const orderNoFromUrl = this.$route.query.orderNo
    if (orderNoFromUrl) {
      this.orderNo = orderNoFromUrl
      this.handleSearch()
    }
  },

  methods: {
    handleSearch () {
      if (!this.orderNo) {
        this.$message.warning('请输入订单号')
        return
      }
      this.loading = true
      queryOrderRiskTrace(this.orderNo).then(({ data, code }) => {
        if (code === 0) {
          this.riskData = data
        }
        this.loading = false
      }).catch(() => {
        this.loading = false
      })
    },

    formatFlightTime (timeStr) {
      if (!timeStr || timeStr.length !== 12) return '-'
      const year = timeStr.substring(0, 4)
      const month = timeStr.substring(4, 6)
      const day = timeStr.substring(6, 8)
      const hour = timeStr.substring(8, 10)
      const minute = timeStr.substring(10, 12)
      return `${year}-${month}-${day} ${hour}:${minute}`
    },

    getRiskLevelClass (level) {
      const classMap = {
        '低风险': 'low-risk',
        '中风险': 'medium-risk',
        '高风险': 'high-risk',
        '极高风险': 'critical-risk',
        'LOW': 'low-risk',
        'MEDIUM': 'medium-risk',
        'HIGH': 'high-risk',
        'CRITICAL': 'critical-risk'
      }
      return classMap[level] || 'medium-risk'
    },

    getScoreTagType (score) {
      if (score >= 60) return 'danger'
      if (score >= 30) return 'warning'
      return 'info'
    },

    getScorePercentage (score) {
      // 确保score是有效数字，默认为0
      const validScore = Number(score) || 0
      // 假设最大风险分为 100，确保至少显示5%
      const percentage = Math.max(Math.min((validScore / 100) * 100, 100), 5)
      return percentage + '%'
    },

    getScoreCellClass (score) {
      if (score >= 60) return 'danger'
      if (score >= 30) return 'warning'
      return 'info'
    }
  }
}
</script>
<style lang="scss" scoped>
// ====================
// 核心设计变量 - 白色主题
// ====================
$color-bg-primary: #ffffff;
$color-bg-secondary: #f8fafc;
$color-bg-card: #ffffff;
$color-bg-elevated: #f1f5f9;

$color-text-primary: #1e293b;
$color-text-secondary: #64748b;
$color-text-muted: #94a3b8;

$color-accent-blue: #3b82f6;
$color-accent-cyan: #06b6d4;
$color-accent-purple: #8b5cf6;

$color-risk-low: #10b981;
$color-risk-medium: #f59e0b;
$color-risk-high: #ef4444;
$color-risk-critical: #dc2626;

$color-glow-blue: rgba(59, 130, 246, 0.15);
$color-glow-cyan: rgba(6, 182, 212, 0.15);

$font-display: 'Segoe UI', system-ui, -apple-system, sans-serif;
$font-mono: 'SF Mono', 'Consolas', 'Monaco', monospace;

$shadow-card: 0 4px 24px rgba(0, 0, 0, 0.06);
$shadow-card-hover: 0 8px 32px rgba(0, 0, 0, 0.1);
$shadow-glow: 0 0 30px $color-glow-blue;
$shadow-accent: 0 4px 20px rgba(59, 130, 246, 0.15);

$border-radius: 16px;
$border-radius-sm: 8px;
$transition-smooth: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);

// ====================
// 全局容器
// ====================
.order-risk-trace-container {
  min-height: 100vh;
  background: linear-gradient(180deg, #f0f4f8 0%, #ffffff 100%);
  padding: 0;
  position: relative;
  overflow-x: hidden;

  // 背景装饰效果
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-image:
      radial-gradient(circle at 20% 30%, rgba(59, 130, 246, 0.03) 0%, transparent 50%),
      radial-gradient(circle at 80% 70%, rgba(6, 182, 212, 0.03) 0%, transparent 50%);
    pointer-events: none;
    z-index: 0;
  }

  > * {
    position: relative;
    z-index: 1;
  }
}

// ====================
// 风控仪表盘
// ====================
.risk-dashboard {
  display: grid;
  grid-template-columns: 1fr 1fr 1fr;
  gap: 16px;
  padding: 20px 32px;
  max-width: 1600px;
  margin: 0 auto;
}

// 主风险卡片
.risk-main-card {
  position: relative;
  background: $color-bg-card;
  border-radius: $border-radius;
  padding: 20px;
  overflow: hidden;
  box-shadow: $shadow-card;
  border: 2px solid transparent;
  transition: $transition-smooth;

  &:hover {
    box-shadow: $shadow-card-hover;
    transform: translateY(-2px);
  }

  // 风险等级颜色变体
  &.low-risk {
    background: linear-gradient(135deg, rgba($color-risk-low, 0.05) 0%, rgba($color-risk-low, 0.02) 100%);
    border-color: rgba($color-risk-low, 0.3);

    .risk-glow {
      background: radial-gradient(circle, rgba($color-risk-low, 0.15) 0%, transparent 70%);
    }

    .risk-pulse {
      border-color: $color-risk-low;
    }

    .risk-icon-wrapper {
      border-color: $color-risk-low;
      color: $color-risk-low;
    }

    .risk-icon {
      color: $color-risk-low;
    }

    .risk-level-in-circle {
      color: $color-risk-low;
    }

    .score-bar-fill {
      background: linear-gradient(90deg, $color-risk-low, lighten($color-risk-low, 8%));
    }
  }

  &.medium-risk {
    background: linear-gradient(135deg, rgba($color-risk-medium, 0.05) 0%, rgba($color-risk-medium, 0.02) 100%);
    border-color: rgba($color-risk-medium, 0.3);

    .risk-glow {
      background: radial-gradient(circle, rgba($color-risk-medium, 0.15) 0%, transparent 70%);
    }

    .risk-pulse {
      border-color: $color-risk-medium;
    }

    .risk-icon-wrapper {
      border-color: $color-risk-medium;
      color: $color-risk-medium;
    }

    .risk-icon {
      color: $color-risk-medium;
    }

    .risk-level-in-circle {
      color: $color-risk-medium;
    }

    .score-bar-fill {
      background: linear-gradient(90deg, $color-risk-medium, lighten($color-risk-medium, 8%));
    }
  }

  &.high-risk {
    background: linear-gradient(135deg, rgba($color-risk-high, 0.05) 0%, rgba($color-risk-high, 0.02) 100%);
    border-color: rgba($color-risk-high, 0.3);

    .risk-glow {
      background: radial-gradient(circle, rgba($color-risk-high, 0.15) 0%, transparent 70%);
    }

    .risk-pulse {
      border-color: $color-risk-high;
    }

    .risk-icon-wrapper {
      border-color: $color-risk-high;
      color: $color-risk-high;
    }

    .risk-icon {
      color: $color-risk-high;
    }

    .risk-level-in-circle {
      color: $color-risk-high;
    }

    .score-bar-fill {
      background: linear-gradient(90deg, $color-risk-high, lighten($color-risk-high, 8%));
    }
  }

  &.critical-risk {
    background: linear-gradient(135deg, rgba($color-risk-critical, 0.05) 0%, rgba($color-risk-critical, 0.02) 100%);
    border-color: rgba($color-risk-critical, 0.3);

    .risk-glow {
      background: radial-gradient(circle, rgba($color-risk-critical, 0.15) 0%, transparent 70%);
    }

    .risk-pulse {
      border-color: $color-risk-critical;
    }

    .risk-icon-wrapper {
      border-color: $color-risk-critical;
      color: $color-risk-critical;
    }

    .risk-icon {
      color: $color-risk-critical;
    }

    .risk-level-in-circle {
      color: $color-risk-critical;
    }

    .score-bar-fill {
      background: linear-gradient(90deg, $color-risk-critical, lighten($color-risk-critical, 8%));
    }
  }

  .risk-glow {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    pointer-events: none;
  }

  .risk-pulse {
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    width: 200px;
    height: 200px;
    border-radius: 50%;
    border: 2px solid;
    opacity: 0;
    animation: pulse-ring 2s ease-out infinite;
    pointer-events: none;
    display: flex;
    align-items: center;
    justify-content: center;

    .risk-level-in-circle {
      font-family: $font-display;
      font-size: 32px;
      font-weight: 700;
      letter-spacing: 2px;
      text-transform: uppercase;
      animation: pulse-text 2s ease-in-out infinite;
    }
  }

  .risk-icon-wrapper {
    position: relative;
    width: 90px;
    height: 90px;
    margin-bottom: 16px;
    border-radius: 50%;
    border: 2px solid;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 4px;
    padding: 8px;
    animation: pulse-border 2s ease-in-out infinite;

    .risk-icon {
      font-size: 28px;
      line-height: 1;
      animation: pulse-icon 2s ease-in-out infinite;
    }

    .risk-label-inline {
      font-family: $font-mono;
      font-size: 8px;
      color: currentColor;
      letter-spacing: 1px;
      text-transform: uppercase;
      font-weight: 600;
      line-height: 1;
      opacity: 0.9;
    }
  }

  .risk-content {
    position: relative;
  }

  .risk-label {
    font-family: $font-mono;
    font-size: 10px;
    color: $color-text-secondary;
    letter-spacing: 2px;
    text-transform: uppercase;
    margin-bottom: 6px;
    font-weight: 600;
  }

  .risk-level-text {
    font-family: $font-display;
    font-size: 28px;
    font-weight: 700;
    color: $color-text-primary;
    margin-bottom: 16px;
    letter-spacing: -1px;
  }

  .risk-score-bar {
    .score-label {
      font-size: 12px;
      color: $color-text-secondary;
      margin-bottom: 6px;
      font-weight: 500;
    }

    .score-value {
      font-family: $font-mono;
      font-size: 26px;
      font-weight: 700;
      color: $color-text-primary;
      margin-bottom: 10px;
    }

    .score-bar-bg {
      height: 10px;
      background: #e8eef5;
      border-radius: 6px;
      overflow: hidden;
      box-shadow: inset 0 1px 3px rgba(0, 0, 0, 0.15);
      position: relative;
      width: 100%;

      .score-bar-fill {
        height: 100%;
        border-radius: 6px;
        transition: width 1s ease-out;
        position: relative;
        min-width: 5%;

        &::after {
          content: '';
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.4), transparent);
          animation: shimmer 2s infinite;
        }
      }
    }
  }
}

// 信息卡片
.info-card {
  background: $color-bg-card;
  border-radius: $border-radius;
  padding: 20px 24px;
  box-shadow: $shadow-card;
  border: 1px solid rgba(59, 130, 246, 0.08);
  transition: $transition-smooth;
  display: flex;
  flex-direction: column;

  &:hover {
    border-color: rgba(59, 130, 246, 0.2);
    box-shadow: $shadow-card-hover;
    transform: translateY(-2px);
  }

  .info-card-header {
    display: flex;
    align-items: center;
    gap: 12px;
    margin-bottom: 16px;
    padding-bottom: 14px;
    border-bottom: 2px solid $color-bg-elevated;

    .info-icon {
      width: 42px;
      height: 42px;
      border-radius: 12px;
      display: flex;
      align-items: center;
      justify-content: center;
      font-size: 20px;

      &.order-icon {
        background: linear-gradient(135deg, rgba($color-accent-blue, 0.15), rgba($color-accent-cyan, 0.15));
        color: $color-accent-blue;
      }

      &.time-icon {
        background: linear-gradient(135deg, rgba($color-accent-purple, 0.15), rgba($color-accent-blue, 0.15));
        color: $color-accent-purple;
      }
    }

    .info-title {
      font-family: $font-display;
      font-size: 17px;
      font-weight: 700;
      color: $color-text-primary;
    }
  }

  .info-body {
    flex: 1;
    display: flex;
    flex-direction: column;
    gap: 14px;

    .info-row {
      display: flex;
      justify-content: space-between;
      align-items: center;

      &.single {
        justify-content: center;
        text-align: center;
      }

      .info-label {
        font-size: 13px;
        color: $color-text-secondary;
        font-weight: 500;
      }

      .info-value {
        font-family: $font-mono;
        font-size: 15px;
        color: $color-text-primary;
        font-weight: 700;

        &.amount {
          font-size: 18px;
          font-weight: 800;
          background: linear-gradient(135deg, $color-accent-cyan, $color-accent-blue);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          background-clip: text;
        }

        &.time {
          font-size: 16px;
          font-weight: 700;
        }
      }
    }
  }
}

// ====================
// 详细信息区域
// ====================
.detail-sections {
  padding: 0 32px 24px;
  max-width: 1600px;
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.detail-card {
  background: $color-bg-card;
  border-radius: $border-radius;
  padding: 20px 24px;
  box-shadow: $shadow-card;
  border: 1px solid rgba(59, 130, 246, 0.08);
  position: relative;
  overflow: hidden;
  transition: $transition-smooth;

  &:hover {
    border-color: rgba(59, 130, 246, 0.15);
    box-shadow: $shadow-card-hover;
  }

  .card-decoration-line {
    position: absolute;
    top: 0;
    left: 0;
    width: 4px;
    height: 100%;
    background: linear-gradient(180deg, $color-accent-blue, $color-accent-cyan);
  }
}

.detail-header {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 16px;

  .header-icon {
    width: 42px;
    height: 42px;
    border-radius: 12px;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 20px;

    &.warning-icon {
      background: linear-gradient(135deg, rgba($color-risk-high, 0.15), rgba($color-risk-medium, 0.15));
      color: $color-risk-high;
    }

    &.location-icon {
      background: linear-gradient(135deg, rgba($color-accent-cyan, 0.15), rgba($color-accent-blue, 0.15));
      color: $color-accent-cyan;
    }

    &.user-icon {
      background: linear-gradient(135deg, rgba($color-accent-purple, 0.15), rgba($color-accent-blue, 0.15));
      color: $color-accent-purple;
    }

    &.flight-icon {
      background: linear-gradient(135deg, rgba($color-accent-blue, 0.15), rgba($color-accent-cyan, 0.15));
      color: $color-accent-blue;
    }

    &.grid-icon {
      background: linear-gradient(135deg, rgba($color-accent-blue, 0.15), rgba($color-accent-purple, 0.15));
      color: $color-accent-blue;
    }
  }

  .header-text {
    h3 {
      font-family: $font-display;
      font-size: 18px;
      font-weight: 700;
      color: $color-text-primary;
      margin-bottom: 2px;
      letter-spacing: -0.3px;
    }

    p {
      font-family: $font-mono;
      font-size: 11px;
      color: $color-text-secondary;
      letter-spacing: 1px;
      font-weight: 500;
    }
  }
}

// ====================
// 表格样式
// ====================
.table-wrapper {
  border-radius: $border-radius-sm;
  overflow: hidden;
  border: 1px solid rgba(59, 130, 246, 0.1);

  .risk-table {
    background: transparent;

    ::v-deep .el-table__header-wrapper {
      background: linear-gradient(180deg, $color-bg-elevated 0%, $color-bg-secondary 100%);

      th {
        background: transparent;
        border-bottom: 2px solid rgba(59, 130, 246, 0.1);
        color: $color-text-primary;
        font-size: 12px;
        font-weight: 700;
        padding: 12px;
        text-transform: uppercase;
        letter-spacing: 0.5px;
      }
    }

    ::v-deep .el-table__body-wrapper {
      td {
        border-bottom: 1px solid rgba(59, 130, 246, 0.06);
        color: $color-text-primary;
        padding: 12px;
        font-weight: 500;
        font-size: 13px;

        &:hover {
          background: rgba(59, 130, 246, 0.03);
        }
      }
    }

    ::v-deep .el-table__row {
      transition: $transition-smooth;

      &:hover {
        background: rgba(59, 130, 246, 0.03);
      }

      &:last-child td {
        border-bottom: none;
      }
    }

    ::v-deep .el-table__empty-block {
      background: transparent;
      color: $color-text-muted;
    }
  }
}

.rule-name-cell {
  display: flex;
  align-items: center;
  gap: 8px;
  font-weight: 600;
  font-size: 13px;

  .rule-dot {
    width: 6px;
    height: 6px;
    border-radius: 50%;
    background: $color-accent-blue;
    box-shadow: 0 0 10px rgba(59, 130, 246, 0.4);

    &.hit {
      background: $color-risk-high;
      box-shadow: 0 0 10px rgba($color-risk-high, 0.4);
    }
  }
}

.score-cell {
  display: inline-flex;
  align-items: baseline;
  gap: 4px;
  padding: 6px 12px;
  border-radius: 8px;
  font-weight: 700;

  .score-number {
    font-family: $font-mono;
    font-size: 18px;
  }

  .score-unit {
    font-size: 11px;
    color: $color-text-secondary;
    font-weight: 500;
  }

  &.danger {
    background: linear-gradient(135deg, rgba($color-risk-high, 0.15), rgba($color-risk-high, 0.08));
    color: $color-risk-high;
    box-shadow: 0 2px 12px rgba($color-risk-high, 0.15);
  }

  &.warning {
    background: linear-gradient(135deg, rgba($color-risk-medium, 0.15), rgba($color-risk-medium, 0.08));
    color: $color-risk-medium;
  }

  &.info {
    background: linear-gradient(135deg, rgba($color-accent-blue, 0.15), rgba($color-accent-blue, 0.08));
    color: $color-accent-blue;
  }
}

.result-badge {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 5px 12px;
  border-radius: 8px;
  font-size: 12px;
  font-weight: 700;
  white-space: nowrap;

  &.hit {
    background: linear-gradient(135deg, rgba($color-risk-high, 0.15), rgba($color-risk-high, 0.08));
    color: $color-risk-high;
  }

  &.safe {
    background: linear-gradient(135deg, rgba($color-risk-low, 0.15), rgba($color-risk-low, 0.08));
    color: $color-risk-low;
  }
}

// ====================
// 信息网格
// ====================
.info-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 12px;

  .info-item-card {
    background: $color-bg-elevated;
    border-radius: $border-radius-sm;
    padding: 14px 16px;
    border: 1px solid rgba(59, 130, 246, 0.08);
    transition: $transition-smooth;
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;

    &:hover {
      border-color: rgba(59, 130, 246, 0.2);
      background: darken($color-bg-elevated, 2%);
      box-shadow: 0 2px 12px rgba(0, 0, 0, 0.05);
    }

    &.full-width {
      grid-column: 1 / -1;
    }

    .item-label {
      font-family: $font-mono;
      font-size: 12px;
      color: $color-text-secondary;
      letter-spacing: 1px;
      text-transform: uppercase;
      font-weight: 600;
      white-space: nowrap;
      flex-shrink: 0;
    }

    .item-value {
      font-family: $font-display;
      font-size: 14px;
      font-weight: 600;
      color: $color-text-primary;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
      text-align: right;

      &.ip, &.phone {
        font-family: $font-mono;
        color: $color-accent-blue;
        font-weight: 700;
      }
    }
  }
}

// ====================
// 航班信息
// ====================
.flight-empty {
  padding: 20px;
  text-align: center;
  color: $color-text-muted;
  background: $color-bg-elevated;
  border-radius: $border-radius-sm;

  .empty-icon {
    font-size: 36px;
    margin-bottom: 10px;
    opacity: 0.4;
  }

  p {
    font-size: 14px;
    font-weight: 500;
  }
}

.flight-section {
  margin-bottom: 12px;

  &:last-child {
    margin-bottom: 0;
  }

  &.return {
    .flight-icon-bg {
      &.return {
        background: linear-gradient(135deg, rgba($color-risk-low, 0.15), rgba($color-accent-cyan, 0.15));
        color: $color-risk-low;
      }
    }
  }

  .flight-section-header {
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 10px 14px;
    background: linear-gradient(135deg, $color-bg-elevated 0%, $color-bg-secondary 100%);
    border-radius: $border-radius-sm;
    margin-bottom: 10px;
    border: 1px solid rgba(59, 130, 246, 0.08);

    .flight-icon-bg {
      width: 36px;
      height: 36px;
      border-radius: 10px;
      display: flex;
      align-items: center;
      justify-content: center;
      font-size: 18px;

      &.departure {
        background: linear-gradient(135deg, rgba($color-accent-blue, 0.15), rgba($color-accent-cyan, 0.15));
        color: $color-accent-blue;
      }
    }

    .flight-header-text {
      .flight-title {
        font-family: $font-display;
        font-size: 15px;
        font-weight: 700;
        color: $color-text-primary;
        margin-bottom: 2px;
      }

      .flight-subtitle {
        font-family: $font-mono;
        font-size: 10px;
        color: $color-text-secondary;
        font-weight: 500;
      }
    }
  }

  .flight-segments {
    display: flex;
    flex-direction: column;
    gap: 10px;
  }

  .flight-segment {
    background: $color-bg-card;
    border-radius: $border-radius-sm;
    padding: 12px 14px;
    border: 1px solid rgba(59, 130, 246, 0.1);
    transition: $transition-smooth;

    &:hover {
      border-color: rgba(59, 130, 246, 0.25);
      box-shadow: 0 4px 16px rgba(59, 130, 246, 0.1);
      transform: translateX(4px);
    }

    .segment-number {
      font-family: $font-mono;
      font-size: 10px;
      color: $color-text-secondary;
      letter-spacing: 1px;
      text-transform: uppercase;
      margin-bottom: 6px;
      font-weight: 600;
    }

    .flight-number-tag {
      margin-bottom: 8px;
    }

    .segment-route {
      display: flex;
      align-items: center;
      justify-content: space-between;
      gap: 12px;

      .airport-dep,
      .airport-arr {
        flex: 1;
        text-align: center;

        .code {
          font-family: $font-display;
          font-size: 20px;
          font-weight: 700;
          color: $color-text-primary;
          margin-bottom: 4px;
          letter-spacing: -0.5px;
        }

        .time {
          font-family: $font-mono;
          font-size: 11px;
          color: $color-text-secondary;
          font-weight: 500;
        }
      }

      .route-arrow {
        flex: 0 0 50px;
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        gap: 4px;

        .arrow-line {
          width: 100%;
          height: 2px;
          background: linear-gradient(90deg, $color-accent-blue, $color-accent-cyan);
          position: relative;
          border-radius: 1px;

          &::after {
            content: '';
            position: absolute;
            top: 0;
            left: 0;
            width: 100%;
            height: 100%;
            background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.6), transparent);
            animation: shimmer 2s infinite;
          }
        }

        i {
          font-size: 18px;
          color: $color-accent-cyan;
        }
      }
    }
  }
}

// ====================
// 空状态
// ====================
.empty-state {
  text-align: center;
  padding: 60px 40px;
  color: $color-text-muted;

  .empty-illustration {
    position: relative;
    width: 100px;
    height: 100px;
    margin: 0 auto 20px;

    i {
      position: relative;
      z-index: 2;
      font-size: 56px;
      color: $color-accent-blue;
    }

    .pulse-ring {
      position: absolute;
      top: 50%;
      left: 50%;
      transform: translate(-50%, -50%);
      width: 100px;
      height: 100px;
      border-radius: 50%;
      border: 2px solid rgba(59, 130, 246, 0.2);
      animation: pulse-ring 3s ease-out infinite;

      &.delay-1 {
        animation-delay: 1s;
      }

      &.delay-2 {
        animation-delay: 2s;
      }
    }
  }

  h2 {
    font-family: $font-display;
    font-size: 24px;
    font-weight: 700;
    color: $color-text-primary;
    margin-bottom: 10px;
  }

  p {
    font-size: 14px;
    color: $color-text-secondary;
    font-weight: 500;
  }
}

// ====================
// 动画
// ====================
@keyframes pulse {
  0%, 100% {
    opacity: 0.6;
    transform: scale(1);
  }
  50% {
    opacity: 0.9;
    transform: scale(1.05);
  }
}

@keyframes pulse-ring {
  0% {
    transform: translate(-50%, -50%) scale(0.6);
    opacity: 0.8;
  }
  100% {
    transform: translate(-50%, -50%) scale(1.4);
    opacity: 0;
  }
}

@keyframes pulse-icon {
  0%, 100% {
    transform: scale(1);
  }
  50% {
    transform: scale(1.08);
  }
}

@keyframes pulse-border {
  0%, 100% {
    transform: scale(1);
    opacity: 1;
  }
  50% {
    transform: scale(1.05);
    opacity: 0.8;
  }
}

@keyframes pulse-text {
  0% {
    transform: scale(0.6);
    opacity: 0;
  }
  50% {
    transform: scale(1);
    opacity: 1;
  }
  100% {
    transform: scale(1.4);
    opacity: 0;
  }
}

@keyframes shimmer {
  0% {
    transform: translateX(-100%);
  }
  100% {
    transform: translateX(100%);
  }
}

// Vue 过渡动画
.fade-enter-active, .fade-leave-active {
  transition: opacity 0.3s ease;
}

.fade-enter, .fade-leave-to {
  opacity: 0;
}

.fade-slide-enter-active {
  transition: all 0.5s ease;
}

.fade-slide-enter {
  opacity: 0;
  transform: translateY(30px);
}

.stagger-enter-active {
  transition: all 0.4s ease;
}

.stagger-enter {
  opacity: 0;
  transform: translateY(20px);
}

// ====================
// 响应式
// ====================
@media (max-width: 1200px) {
  .risk-dashboard {
    grid-template-columns: 1fr;
  }
}

@media (max-width: 768px) {
  .risk-dashboard,
  .detail-sections {
    padding-left: 16px;
    padding-right: 16px;
  }

  .info-grid {
    grid-template-columns: 1fr;
  }

  .detail-card {
    padding: 16px 18px;
  }
}
</style>
